"use strict";
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.describeImportCompliance = exports.unsubscribeLinkNotes = exports.plainCompliance = exports.invisibleLinkNotes = exports.isInvisibleLinkNote = exports.invisibleLinkNote = exports.isUnsubscribeHref = exports.repairUnsubscribeLinks = exports.repairUnsubscribeHref = exports.checkEmailHasAddress = exports.checkEmailHasUnsubscribeTagAnywhere = exports.checkEmailHasUnsubscribe = exports.auditUnsubscribe = exports.hrefIsUrlTag = void 0;
const email_service_links_1 = require("./email_service_links");
const marked_text_1 = require("./marked_text");
const html_scan_1 = require("./html_scan");
/**
 * Does a reader of this email have a WORKING way to unsubscribe through
 * Encharge?
 *
 * This used to be a substring search for the tag anywhere in the HTML, so an
 * email passed with the tag in a comment, in alt text, or written into an
 * href in its whole-link form ({{person.unsubscribeLink}} renders a complete
 * `<a>`, so inside an href it breaks the link it is in). An imported email
 * whose footer still pointed at another platform's [UNSUBSCRIBE_URL] was then
 * one short step from a send with a dead unsubscribe link.
 *
 * An HTML email passes when one of these reaches the reader:
 *   1. an `<a>` whose href IS a URL tag ({{person.unsubscribeURL}},
 *      unsubscribeAllURL or managePreferencesURL), STARTS with one followed
 *      by & or # (more query parameters or a fragment; not ?, because the tag
 *      already prints a query string), or is a Liquid if, unless or case
 *      that ends in an else and whose every branch is a URL tag (no filter
 *      on any of them: a filter can change the address); and that has text
 *      or an image to click. This is what the editor's special links insert.
 *      The `-` whitespace control ({{- ... -}}, {%- ... -%}) is allowed in
 *      every tag;
 *   2. a whole-link tag ({{person.unsubscribeLink}}, unsubscribeAllLink,
 *      managePreferencesLink) in the copy, which renders its own `<a>`. This
 *      is what the editors' merge-tag menus insert;
 *   3. a URL tag printed in the copy, which the editor's "Unsubscribe URL"
 *      merge tag inserts: the reader sees the address. The send path makes
 *      it a link (HTMLRewrite.convertPlainTextUrlsToLinks) only while click
 *      tracking is on (no skipLinkTracking: an account flag and the
 *      transactional API's linkTracking: false turn it off); otherwise the
 *      reader's mail client may link it. It counts either way: an email
 *      with one of the unsubscribe tags in it has an unsubscribe, as it
 *      always had.
 *   4. a link the reader can still unsubscribe through, though not as
 *      well as it should, which passes with a warning (linkWorks false):
 *      the URL tag followed by a second ? (the page shows the preferences
 *      view, one more click), or the URL tag as the
 *      value of a query parameter of another address (the customer's own
 *      redirect, which then has to send the reader on).
 * Held (no shape above): the whole-link tag written into an href (a link
 * inside a link, which the browser reads as garbage), the tag only in a
 * comment, a script, a style or the title, a link with nothing to click,
 * an if with no else (some readers get an empty href), and the tag passed
 * through a filter (it can change the address). The rule (Vince,
 * 2026-10-02): hold an email when the reader cannot reach Encharge's
 * unsubscribe page at all, warn when they can but not as well.
 * Comments (Outlook conditionals included), scripts, styles, the title and
 * attribute values never count.
 * Nor does what provably never reaches a reader (V2-L5, withoutNeverRendered):
 * a Liquid branch whose condition is a constant that never lets it render
 * ({% if false %}, {% unless true %}, the rest of an if after a branch that is
 * always true), a {% capture %} whose variable is never read, and an element
 * hidden inline (display:none, visibility:hidden, the hidden attribute) that
 * no stylesheet rule can show again. A link only some readers get (a
 * condition on the contact, a phone-only copy a media query shows) still
 * counts: proving it reaches no one would take more than the markup says.
 *
 * The manage-preferences tags count, as they always have: that page lets the
 * reader unsubscribe from everything.
 *
 * A plain-text email (type "text") has no markup to put a link in, so any of
 * the tags anywhere in it counts, as before.
 */
// A URL tag as written in a template: spaces allowed, no filter. A filter can
// change the address it prints ({{ person.unsubscribeURL | prepend: "https://x.example/?u=" }}),
// so a tag with one does not count.
const URL_TAG_SOURCE = "\\{\\{-?\\s*person\\.(?:unsubscribeURL|unsubscribeAllURL|managePreferencesURL)\\s*-?\\}\\}";
const URL_TAG = new RegExp(`^${URL_TAG_SOURCE}$`);
/**
 * an href that starts with a URL tag and goes on with more query parameters (&) or a fragment (#). Not `?`: the tag
 * already prints a query string (`...?t=TOKEN&mode=category`), so a second `?` glues onto its last value and the
 * page falls back to the preferences view instead of the one-click unsubscribe.
 */
const URL_TAG_THEN_QUERY = new RegExp(`^${URL_TAG_SOURCE}[&#]`);
/** an href that is a URL tag followed by a second `?` (the tag already prints a query string) */
const URL_TAG_THEN_QUESTION = new RegExp(`^${URL_TAG_SOURCE}\\?`);
/**
 * an href that starts with a URL tag passed through a filter ({{ person.unsubscribeURL | prepend: "x" }}). Held as a
 * rule, whatever the filter: a literal address around the tag can be read here (URL_TAG_IN_ADDRESS), the output of
 * a filter (prepend, replace, remove, a custom one, a chain) would have to be run, so the same redirect written with
 * prepend is held where its literal form passes with a warning. Writing the redirect out makes it pass.
 */
const FILTERED_URL_TAG = /^\{\{-?\s*person\.(?:unsubscribeURL|unsubscribeAllURL|managePreferencesURL)\s*\|/;
/** an unsubscribe tag written in another case ({{PERSON.UNSUBSCRIBEURL}}): Liquid reads names as written, so it prints nothing */
const MISCASED_TAG = /\{\{-?\s*person\.(unsubscribeURL|unsubscribeAllURL|managePreferencesURL|unsubscribeLink|unsubscribeAllLink|managePreferencesLink)\s*-?\}\}/i;
const EXACT_TAG = /\{\{-?\s*person\.(?:unsubscribeURL|unsubscribeAllURL|managePreferencesURL|unsubscribeLink|unsubscribeAllLink|managePreferencesLink)\s*-?\}\}/;
/**
 * The `{% ... %}` tags of an href, in order, read as Liquid reads them: a tag
 * ends at the first `%}` outside a quoted string (so `{% if a == "50%}" %}` is
 * one tag), and the `-` whitespace control is not part of the name or the
 * markup. undefined when a tag or a quoted string is never closed. One pass.
 */
const readLiquidTags = (s) => {
    const tags = [];
    let i = s.indexOf("{%");
    while (i >= 0) {
        let quote = "";
        let end = -1;
        for (let j = i + 2; j < s.length; j += 1) {
            const c = s[j];
            if (quote) {
                if (c === quote)
                    quote = "";
            }
            else if (c === '"' || c === "'") {
                quote = c;
            }
            else if (c === "%" && s[j + 1] === "}") {
                end = j + 2;
                break;
            }
        }
        if (end < 0)
            return undefined;
        const inner = s
            .slice(i + 2, end - 2)
            .replace(/^-/, "")
            .replace(/-$/, "")
            .trim();
        const m = /^(\w+)([\s\S]*)$/.exec(inner);
        if (!m)
            return undefined;
        tags.push({ name: m[1], markup: m[2].trim(), start: i, end });
        i = s.indexOf("{%", end);
    }
    return tags;
};
/** The block tags an href can branch with, and the tag that closes each. */
const LIQUID_BLOCKS = {
    if: { end: "endif", branches: ["elsif", "else"] },
    unless: { end: "endunless", branches: ["elsif", "else"] },
    case: { end: "endcase", branches: ["when", "else"] },
};
/** One literal of a `when`: a quoted string or a number. */
const WHEN_LITERAL = /^("[^"]*"|'[^']*'|-?\d+(?:\.\d+)?)\s*/;
/** The values of a `when` ('de', "fr" or 'es', 1), as text; undefined when one is not a literal (a variable). */
const whenValues = (markup) => {
    const values = [];
    let rest = markup;
    for (;;) {
        const m = WHEN_LITERAL.exec(rest);
        if (!m)
            return undefined;
        values.push(m[1].replace(/^["']|["']$/g, ""));
        rest = rest.slice(m[0].length);
        if (!rest)
            return values;
        const sep = /^(?:,|or\b)\s*/.exec(rest);
        if (!sep)
            return undefined;
        rest = rest.slice(sep[0].length);
    }
};
/**
 * An href that is a Liquid if, unless or case giving every reader exactly one
 * URL tag: every branch is a URL tag, there is one else and it is the last
 * branch (with no else, whoever no branch matches gets an empty href; Liquid
 * renders every else it meets), and nothing is nested. A case renders EVERY
 * when that matches, so its when values must be literals and no value may
 * appear in two whens: two matching branches would glue two addresses
 * together. Anything else is held.
 */
const isLiquidUrlTagChoice = (h) => {
    if (!h.startsWith("{%"))
        return false;
    const tags = readLiquidTags(h);
    if (!tags || tags.length < 3)
        return false;
    const open = tags[0];
    const close = tags[tags.length - 1];
    const block = LIQUID_BLOCKS[open.name];
    if (!block || close.name !== block.end || close.end !== h.length || !open.markup)
        return false;
    const middle = tags.slice(1, -1);
    if (middle.some((t) => !block.branches.includes(t.name)))
        return false;
    // one else, and last: every reader gets one of the branches
    const elses = middle.filter((t) => t.name === "else");
    if (elses.length !== 1 || middle[middle.length - 1].name !== "else" || elses[0].markup)
        return false;
    const bodies = [];
    if (open.name === "case") {
        // what is written before the first when is never rendered: only white space is allowed there
        if (h.slice(open.end, middle[0].start).trim())
            return false;
        const seen = new Set();
        for (const t of middle) {
            if (t.name !== "when")
                continue;
            const values = whenValues(t.markup);
            if (!values)
                return false;
            for (const v of Array.from(new Set(values))) {
                if (seen.has(v))
                    return false;
                seen.add(v);
            }
        }
    }
    else {
        if (middle.some((t) => t.name === "elsif" && !t.markup))
            return false;
        bodies.push(h.slice(open.end, middle[0].start));
    }
    middle.forEach((t, k) => bodies.push(h.slice(t.end, (middle[k + 1] || close).start)));
    return bodies.every((body) => URL_TAG.test(body.trim()));
};
/**
 * An href that leads to Encharge's own page: a URL tag alone, a URL tag with a
 * query string or fragment after it ({{person.unsubscribeURL}}&utm_source=x),
 * or a Liquid if, unless or case that ends in an else and whose every branch is
 * a URL tag (isLiquidUrlTagChoice). A URL tag buried in another address
 * (https://x.example/?next={{...}}) or passed through a filter does not count.
 * The `-` whitespace control ({{- ... -}}, {%- ... -%}) is allowed throughout.
 */
const hrefIsUrlTag = (href) => {
    const h = href.trim();
    if (URL_TAG.test(h) || URL_TAG_THEN_QUERY.test(h))
        return true;
    return isLiquidUrlTagChoice(h);
};
exports.hrefIsUrlTag = hrefIsUrlTag;
const URL_TAG_IN_COPY = /\{\{-?\s*person\.(unsubscribeURL|unsubscribeAllURL|managePreferencesURL)\s*-?\}\}/;
const LINK_TAG_IN_COPY = /\{\{-?\s*person\.(unsubscribeLink|unsubscribeAllLink|managePreferencesLink)\s*-?\}\}/;
const ANY_TAG = /\{\{-?\s*person\.(unsubscribeURL|unsubscribeAllURL|managePreferencesURL|unsubscribeLink|unsubscribeAllLink|managePreferencesLink)\s*-?\}\}/;
/**
 * an href that is another address with the URL tag, no filter, as the value of one of its query parameters
 * (the customer's redirect: https://r.example/go?to={{person.unsubscribeURL}}), the one place another site can send
 * the reader on from. The tag in the address's path (https://x.example/{{...}}) makes a page on that site, and the
 * reader never reaches Encharge: held. The scheme in any case (HTTPS:// is legal), the tag only as Liquid reads it:
 * {{PERSON.UNSUBSCRIBEURL}} renders empty. Earlier parameters may hold merge tags of their own
 * (https://r.example/go?email={{person.email}}&to={{person.unsubscribeURL}}); the path may not. Each run of the
 * parameters group ends at an & its characters cannot hold, so the pattern reads the address once.
 */
const URL_TAG_IN_ADDRESS = /^[Hh][Tt][Tt][Pp][Ss]?:\/\/[^\s{}"'?#]*\?(?:(?:[^\s{}"'#&]|\{\{[^{}]*\}\})*&)*[^\s{}"'#=&]+=\{\{-?\s*person\.(?:unsubscribeURL|unsubscribeAllURL|managePreferencesURL)\s*-?\}\}/;
const LINK_TAG_IN_HREF = /^\{\{-?\s*person\.(\w+)Link\s*-?\}\}$/;
/** Plain words: a customer value as it is (see marked_text quote() for the marked form). */
const asIs = (value) => value;
/**
 * The URL tag to write instead of an unsubscribe tag as written, in any case and either family
 * (unsubscribeLink, UNSUBSCRIBEURL, person.UnsubscribeAllLink...): always the *URL form, the one that works in an
 * href, spelled as Liquid reads it. undefined for a name outside the three.
 */
const urlTagFor = (name) => {
    const base = name.toLowerCase().replace(/(link|url)$/, "");
    const right = { unsubscribe: "unsubscribeURL", unsubscribeall: "unsubscribeAllURL", managepreferences: "managePreferencesURL" }[base];
    return right ? `{{person.${right}}}` : undefined;
};
/** Why a link does not work, as the end of a sentence; `q` quotes the link's destination. */
const why = (href, clickable, q = asIs) => {
    const h = href.trim();
    const shown = (0, html_scan_1.clipText)(h, 120);
    if ((0, exports.hrefIsUrlTag)(h) && !clickable)
        return "has no text or image to click";
    // first: a mis-cased tag prints nothing at all, whichever family it is. The advice is given only when writing
    // the *URL form there passes (the tag alone, or where shape 4 takes it); otherwise the reason below applies
    // (an href that starts with # goes nowhere whatever the tag)
    const miscased = MISCASED_TAG.exec(h);
    const fixed = miscased && !EXACT_TAG.test(h) ? urlTagFor(miscased[1]) : undefined;
    if (miscased && fixed) {
        // every occurrence: an if/else writes the tag once per branch
        const swapped = h.split(miscased[0]).join(fixed);
        if ((0, exports.hrefIsUrlTag)(swapped) || URL_TAG_IN_ADDRESS.test(swapped) || URL_TAG_THEN_QUESTION.test(swapped))
            return `writes the tag as ${q(miscased[0])}, which prints nothing: tag names are case-sensitive (write ${fixed})`;
    }
    const whole = LINK_TAG_IN_HREF.exec(h);
    if (whole) {
        const use = urlTagFor(`${whole[1]}Link`);
        return `uses ${q(h)}, which prints a whole link of its own and breaks inside an href${use ? ` (use ${use})` : ""}`;
    }
    if (!h || /^#/.test(h))
        return "goes nowhere";
    if (URL_TAG_THEN_QUESTION.test(h))
        return `adds a second ? after the tag, which already prints its own query string, so the reader lands on the preferences page instead of the one-click unsubscribe (write & instead)`;
    if (FILTERED_URL_TAG.test(h))
        return "passes the tag through a filter, which can change the address it prints";
    if (URL_TAG_IN_ADDRESS.test(h))
        return `puts the tag inside another address (${q(shown)}), so the reader reaches Encharge's unsubscribe page only if that site sends them on (use the tag alone)`;
    // Encharge's own stop page tag, however it is spaced (classifyServiceLink reads it as ENCHARGE_URL_TAG does). It
    // is filled on every send (entities/service.ts), so it is no unfilled placeholder: it is just not an unsubscribe.
    const own = h.includes("stopTrackingURL") ? (0, email_service_links_1.classifyServiceLink)(h, "") : undefined;
    if (own && own.by === "encharge-tag" && own.kind === "stopTracking")
        return `points at ${q(shown)}, the Refuse email tracking page, which stops tracking but does not unsubscribe`;
    if ((0, email_service_links_1.placeholderName)(h))
        return `points at ${q(shown)}, a placeholder Encharge does not fill`;
    if (/^mailto:/i.test(h))
        return `sends an email to ${q(shown.replace(/^mailto:/i, ""))}, which Encharge cannot act on`;
    return `points at ${q(shown)}, not at Encharge's unsubscribe page, so Encharge never records the opt-out`;
};
/** Elements whose content never reaches a reader as the email's own markup (fallback content, text, a template). */
const INERT_ELEMENTS = new Set(["iframe", "noembed", "noframes", "noscript", "template", "textarea", "title", "xmp", "style", "script"]);
const LIQUID_BLOCK_OPEN = /\{%-?\s*(comment|raw)\s*-?%\}/g;
/**
 * Where the email's markup is not read: Liquid {% comment %} (renders
 * nothing) and the content of the inert elements are blanked whole. A
 * {% raw %} block prints its content as typed, so its markup is real and its
 * Liquid tags are the only thing that is not: the audit blanks just the `{{`
 * and `{%` openers inside it. A block that is never closed runs to the end,
 * as nothing after it renders. An inert element opened inside a {% comment %}
 * opens nothing, as the comment renders nothing.
 */
const inertRanges = (body) => {
    const ranges = [];
    const comments = [];
    LIQUID_BLOCK_OPEN.lastIndex = 0;
    let m;
    // eslint-disable-next-line no-cond-assign
    while ((m = LIQUID_BLOCK_OPEN.exec(body))) {
        const end = new RegExp(`\\{%-?\\s*end${m[1]}\\s*-?%\\}`, "g");
        end.lastIndex = m.index + m[0].length;
        const close = end.exec(body);
        const to = close ? close.index + close[0].length : body.length;
        ranges.push([m.index, to, m[1] === "raw" ? "raw" : "blank"]);
        if (m[1] === "comment")
            comments.push([m.index, to]);
        LIQUID_BLOCK_OPEN.lastIndex = to;
    }
    const spans = (0, email_service_links_1.markupSpans)(body);
    // the comment ranges are in order and so are the spans: one pointer walks both
    let c = 0;
    const hidden = (at) => {
        while (c < comments.length && comments[c][1] <= at)
            c += 1;
        return c < comments.length && comments[c][0] <= at;
    };
    // each inert element's end tag, in order: an opening tag takes the first one after it (one pass, not a search per opening tag)
    const closers = new Map();
    const opened = [];
    for (const t of spans) {
        if (t.kind !== "tag" || !t.name || !INERT_ELEMENTS.has(t.name) || hidden(t.start))
            continue;
        if (t.closing) {
            const list = closers.get(t.name) || [];
            list.push(t.start);
            closers.set(t.name, list);
        }
        else {
            opened.push(t);
        }
    }
    const next = new Map();
    for (const t of opened) {
        const list = closers.get(t.name) || [];
        let i = next.get(t.name) || 0;
        while (i < list.length && list[i] < t.end)
            i += 1;
        next.set(t.name, i);
        ranges.push([t.end, i < list.length ? list[i] : body.length, "blank"]);
    }
    return ranges;
};
/** The markup with its inert places blanked out (same length, so positions hold). */
const withoutInert = (body) => {
    const all = inertRanges(body);
    if (!all.length)
        return body;
    const ranges = all.filter((r) => r[2] === "blank").sort((a, b) => a[0] - b[0]);
    // merged, so that a run of unclosed elements (each reaching the end) is blanked once
    let out = "";
    let at = 0;
    for (const [from, to] of ranges) {
        if (to <= at)
            continue;
        const start = Math.max(from, at);
        out += body.slice(at, start) + body.slice(start, to).replace(/\S/g, " ");
        at = to;
    }
    out += body.slice(at);
    // a raw block prints its markup, so only the Liquid tags in it are blanked (one pass: raw blocks do not overlap)
    const raws = all.filter((r) => r[2] === "raw");
    if (!raws.length)
        return out;
    const pieces = [];
    let upTo = 0;
    for (const [from, to] of raws) {
        pieces.push(out.slice(upTo, from), out.slice(from, to).replace(/\{[{%]/g, "  "));
        upTo = to;
    }
    pieces.push(out.slice(upTo));
    return pieces.join("");
};
// ---------------------------------------------------------------------------
// What never reaches a reader (V2-L5)
/** Liquid's falsy literals. Every other literal is truthy, 0 and "" included (liquidjs's default, which the send uses). */
const LIQUID_FALSY = /^(?:false|nil|null)$/;
const LIQUID_TRUTHY = /^(?:true|-?\d+(?:\.\d+)?|"[^"]*"|'[^']*')$/;
/** A condition that is one literal: always true, never true, or undefined when it reads anything. */
const constantCondition = (markup) => {
    if (LIQUID_FALSY.test(markup))
        return false;
    return LIQUID_TRUTHY.test(markup) ? true : undefined;
};
/** The Liquid blocks the pass pairs, and the tag that closes each. */
const LIQUID_PAIRED = {
    if: "endif",
    unless: "endunless",
    case: "endcase",
    for: "endfor",
    capture: "endcapture",
    tablerow: "endtablerow",
};
const CAPTURE_NAME = /^([A-Za-z_][\w-]*)$/;
/** Past these, the rest is read as rendering (as before): the passes stay bounded on any markup. */
const MAX_CAPTURES = 200;
const MAX_HIDDEN = 500;
/** The text with each range's characters blanked (same length, so positions hold). Ranges may overlap. */
const blankRanges = (text, ranges) => {
    if (!ranges.length)
        return text;
    let out = "";
    let at = 0;
    for (const [from, to] of ranges.slice().sort((a, b) => a[0] - b[0])) {
        if (to <= at)
            continue;
        const start = Math.max(from, at);
        out += text.slice(at, start) + text.slice(start, to).replace(/\S/g, " ");
        at = to;
    }
    return out + text.slice(at);
};
/** Is the position inside one of the markup's tags (an attribute value)? The tags come in order. */
const insideTagOf = (tags) => (at) => {
    let lo = 0;
    let hi = tags.length - 1;
    while (lo <= hi) {
        const mid = Math.floor((lo + hi) / 2);
        if (tags[mid].end <= at)
            lo = mid + 1;
        else if (tags[mid].start > at)
            hi = mid - 1;
        else
            return true;
    }
    return false;
};
/**
 * The bodies of the Liquid blocks that never render: an if or elsif branch
 * whose condition is false, nil or null, an unless whose condition is a
 * truthy literal, every branch after one that is always true, and a capture
 * whose variable no other Liquid reads. case, for and every condition that
 * reads something are left alone, as is a block written inside a tag (an
 * href's if/else is the attribute's: hrefIsUrlTag reads it). Liquid that does
 * not pair up is read as written (the save's render check refuses it).
 */
const neverRenderedLiquid = (body) => {
    if (!body.includes("{%"))
        return [];
    const tags = readLiquidTags(body);
    if (!tags)
        return [];
    const insideTag = insideTagOf((0, email_service_links_1.markupSpans)(body).filter((t) => t.kind === "tag"));
    const stack = [];
    const ranges = [];
    const captures = [];
    for (const t of tags) {
        if (LIQUID_PAIRED[t.name]) {
            stack.push({ open: t, branches: [] });
            continue;
        }
        const top = stack[stack.length - 1];
        if (t.name === "elsif" || t.name === "else" || t.name === "when") {
            if (!top)
                return [];
            top.branches.push(t);
            continue;
        }
        if (!t.name.startsWith("end"))
            continue;
        if (!top || t.name !== LIQUID_PAIRED[top.open.name])
            return [];
        stack.pop();
        const parts = [top.open, ...top.branches];
        if (insideTag(t.start) || parts.some((p) => insideTag(p.start)))
            continue;
        const bodyOf = (k) => [
            parts[k].end,
            (parts[k + 1] || t).start,
        ];
        if (top.open.name === "capture") {
            const name = CAPTURE_NAME.exec(top.open.markup);
            if (name && top.branches.length === 0 && captures.length < MAX_CAPTURES)
                captures.push({ name: name[1], from: top.open.end, to: t.start });
            continue;
        }
        if (top.open.name !== "if" && top.open.name !== "unless")
            continue;
        // an earlier branch always renders: none after it does
        let decided = false;
        parts.forEach((p, k) => {
            let renders = true;
            if (decided)
                renders = false;
            else if (p.name !== "else") {
                let c = constantCondition(p.markup);
                if (p.name === "unless" && c !== undefined)
                    c = !c;
                renders = c !== false;
                if (c === true)
                    decided = true;
            }
            if (!renders)
                ranges.push(bodyOf(k));
        });
    }
    if (stack.length)
        return [];
    if (captures.length) {
        // every name any other Liquid reads: outputs and tags, the captures' own names aside
        const reads = new Set();
        const note = (text) => {
            for (const w of text.match(/[A-Za-z_][\w-]*/g) || [])
                reads.add(w);
        };
        for (const t of tags)
            if (t.name !== "capture")
                note(t.markup);
        for (let i = body.indexOf("{{"); i >= 0;) {
            const end = body.indexOf("}}", i + 2);
            if (end < 0)
                break;
            note(body.slice(i + 2, end));
            i = body.indexOf("{{", end + 2);
        }
        for (const c of captures)
            if (!reads.has(c.name))
                ranges.push([c.from, c.to]);
    }
    return ranges;
};
/** Elements with no content: hidden, the tag itself is what goes. */
const VOID_ELEMENTS = new Set([
    "area",
    "base",
    "br",
    "col",
    "embed",
    "hr",
    "img",
    "input",
    "link",
    "meta",
    "source",
    "track",
    "wbr",
]);
const BLOCK_STARTS = "address article aside blockquote details dialog div dl fieldset figcaption figure footer form h1 h2 h3 h4 h5 h6 header hgroup hr main menu nav ol p pre section table ul";
/**
 * Elements whose end tag may be left out, and the start tags that end them
 * first (the HTML parser's rules, in short). The table and list ones count
 * only at the element's own depth: a nested table's cells do not end it.
 */
const ENDED_BY = {
    p: { by: BLOCK_STARTS.split(" ") },
    li: { by: ["li"], depth: "list" },
    dt: { by: ["dt", "dd"], depth: "dl" },
    dd: { by: ["dt", "dd"], depth: "dl" },
    td: { by: ["td", "th", "tr", "tbody", "thead", "tfoot"], depth: "table" },
    th: { by: ["td", "th", "tr", "tbody", "thead", "tfoot"], depth: "table" },
    tr: { by: ["tr", "tbody", "thead", "tfoot"], depth: "table" },
    tbody: { by: ["tbody", "thead", "tfoot"], depth: "table" },
    thead: { by: ["tbody", "thead", "tfoot"], depth: "table" },
    tfoot: { by: ["tbody", "thead", "tfoot"], depth: "table" },
};
/** Optional end tags whose rules are not worth reading here: such an element is never taken as hidden. */
const NOT_READ = new Set([
    "option",
    "optgroup",
    "colgroup",
    "caption",
    "rb",
    "rt",
    "rtc",
    "rp",
]);
const CONTAINERS = {
    table: "table",
    ul: "list",
    ol: "list",
    menu: "list",
    dl: "dl",
};
/** A property's value as the browser takes it from one style attribute: the last one, an !important one over the rest. */
const declared = (style, prop) => {
    let found;
    for (const d of style.split(";")) {
        const colon = d.indexOf(":");
        if (colon < 0 || d.slice(0, colon).trim().toLowerCase() !== prop)
            continue;
        const raw = d
            .slice(colon + 1)
            .trim()
            .toLowerCase();
        const important = /!\s*important$/.test(raw);
        if (found && found.important && !important)
            continue;
        found = { value: raw.replace(/!\s*important$/, "").trim(), important };
    }
    return found;
};
const reach = () => ({
    classes: new Set(),
    ids: new Set(),
    tags: new Set(),
});
/**
 * The class or id name that starts at `at` (just past its . or #), with its
 * CSS escapes read as the browser reads them (`\:` is ":", `\31 ` is "1"), and
 * where it ends: Tailwind's .sm\:block is the class sm:block (F33).
 */
const selectorName = (sel, at) => {
    let name = "";
    let k = at;
    while (k < sel.length) {
        const ch = sel[k];
        if (ch === "\\") {
            let hex = "";
            while (hex.length < 6 && /[0-9a-f]/i.test(sel[k + 1 + hex.length] || ""))
                hex += sel[k + 1 + hex.length];
            if (hex) {
                const code = parseInt(hex, 16);
                name +=
                    code > 0 && code <= 0x10ffff && (code < 0xd800 || code > 0xdfff)
                        ? String.fromCodePoint(code)
                        : "�";
                k += 1 + hex.length;
                // one blank after a hex escape ends it
                if (/\s/.test(sel[k] || ""))
                    k += 1;
            }
            else if (k + 1 < sel.length && sel[k + 1] !== "\n") {
                name += sel[k + 1];
                k += 2;
            }
            else
                break;
        }
        else if (/[\w-]/.test(ch) || ch.charCodeAt(0) >= 0x80) {
            name += ch;
            k += 1;
        }
        else
            break;
    }
    return { name: name.toLowerCase(), end: k };
};
const extendReach = (r, selector) => {
    const sel = selector.toLowerCase();
    if (/[*[]/.test(sel))
        r.tags.add("*");
    for (let k = 0; k < selector.length; k += 1) {
        const ch = selector[k];
        if (ch !== "." && ch !== "#")
            continue;
        const { name, end } = selectorName(selector, k + 1);
        if (name)
            (ch === "." ? r.classes : r.ids).add(name);
        k = end - 1;
    }
    for (const m of sel.match(/(?:^|[\s>+~,(])[a-z][\w-]*/g) || [])
        r.tags.add(m.replace(/^[\s>+~,(]/, ""));
};
/**
 * A stylesheet declaration that sets display to something other than none,
 * and whether it is !important; undefined for any other declaration, and for
 * one the browser drops (a "!" not followed by important alone).
 */
const displayShown = (declaration) => {
    const colon = declaration.indexOf(":");
    if (colon < 0 ||
        declaration.slice(0, colon).trim().toLowerCase() !== "display")
        return undefined;
    const rest = declaration.slice(colon + 1);
    const bang = rest.indexOf("!");
    if (bang >= 0 &&
        rest
            .slice(bang + 1)
            .trim()
            .toLowerCase() !== "important")
        return undefined;
    const value = (bang < 0 ? rest : rest.slice(0, bang)).trim().toLowerCase();
    if (!value || value === "none")
        return undefined;
    return { important: bang >= 0 };
};
/**
 * What the email's stylesheets can show again, read from every <style> (an
 * Outlook-only one too: reading more only hides less):
 *   - `important`: rules with a display other than none and !important,
 *     which beat an inline display:none;
 *   - `display`: rules with any display other than none, which beat the
 *     hidden attribute;
 *   - `visible`: some rule sets visibility:visible, which can show an
 *     element under visibility:hidden.
 */
const sheetReveals = (html) => {
    const important = reach();
    const display = reach();
    let visible = false;
    const lower = html.toLowerCase();
    for (let i = lower.indexOf("<style"); i >= 0;) {
        const open = lower.indexOf(">", i);
        if (open < 0)
            break;
        const close = lower.indexOf("</style", open);
        let css = html.slice(open + 1, close < 0 ? html.length : close);
        // without its comments
        let plain = "";
        for (let at = 0; at < css.length;) {
            const c = css.indexOf("/*", at);
            if (c < 0) {
                plain += css.slice(at);
                break;
            }
            plain += css.slice(at, c);
            const e = css.indexOf("*/", c + 2);
            at = e < 0 ? css.length : e + 2;
        }
        css = plain;
        // each rule's selector and declarations, at any @media depth
        let selectorFrom = 0;
        let selector = "";
        let declFrom = -1;
        for (let k = 0; k < css.length; k += 1) {
            const ch = css[k];
            if (ch === "{") {
                // an @media's prelude too: only a block with no block inside it is a rule
                selector = css.slice(selectorFrom, k);
                declFrom = k + 1;
                selectorFrom = k + 1;
            }
            else if (ch === "}") {
                if (declFrom >= 0) {
                    const decls = css.slice(declFrom, k);
                    if (/visibility\s*:\s*visible/i.test(decls))
                        visible = true;
                    // read without a regex (blanks around a value backtracked a regex
                    // in cubic time, F03), and the selector once per rule, not once
                    // per declaration
                    let shows = false;
                    let showsImportant = false;
                    for (const d of decls.split(";")) {
                        const shown = displayShown(d);
                        if (shown === undefined)
                            continue;
                        shows = true;
                        if (shown.important)
                            showsImportant = true;
                    }
                    if (shows)
                        extendReach(display, selector);
                    if (showsImportant)
                        extendReach(important, selector);
                }
                declFrom = -1;
                selectorFrom = k + 1;
            }
        }
        i = close < 0 ? -1 : lower.indexOf("<style", close + 7);
    }
    return { important, display, visible };
};
/** Can a rule with this reach match the element (by a class, its id or its name)? */
const reaches = (r, tag, name) => {
    if (r.tags.has("*") || r.tags.has(name))
        return true;
    const id = (0, email_service_links_1.attrSpan)(tag, "id");
    if (id && r.ids.has(id.value.trim().toLowerCase()))
        return true;
    const cls = (0, email_service_links_1.attrSpan)(tag, "class");
    return Boolean(cls &&
        cls.value
            .toLowerCase()
            .split(/\s+/)
            .some((c) => r.classes.has(c)));
};
/** Is there a sorted number strictly between from and to? */
const someBetween = (sorted, from, to) => {
    if (!sorted || !sorted.length)
        return false;
    let lo = 0;
    let hi = sorted.length;
    while (lo < hi) {
        const mid = Math.floor((lo + hi) / 2);
        if (sorted[mid] <= from)
            lo = mid + 1;
        else
            hi = mid;
    }
    return lo < sorted.length && sorted[lo] < to;
};
const MAY_HIDE = /\shidden\b|display\s*:\s*none|visibility\s*:\s*hidden/i;
/**
 * The content of each element hidden inline that nothing shows again: a
 * display:none (unless a stylesheet's !important display rule can match the
 * element), a visibility:hidden (unless any rule, or an element inside it,
 * sets visibility:visible), the hidden attribute (unless a stylesheet's
 * display rule can match it). mso-hide:all alone hides nothing: it marks the
 * half every client but Outlook shows. A tag written with Liquid in it is
 * the Liquid's to decide, and is not read. An element is taken to end at its
 * own end tag; one whose end tag may be left out and that another start tag
 * ends first, or that has none, is not read as hidden.
 */
const hiddenInline = (body, html) => {
    if (!MAY_HIDE.test(body))
        return [];
    const spans = (0, email_service_links_1.markupSpans)(body);
    const sheet = sheetReveals(html);
    // each start tag's index by name (and container depth), for "another start tag ends it first"
    const startsAt = new Map();
    const depth = { table: 0, list: 0, dl: 0 };
    const depthAt = new Array(spans.length);
    const matched = new Array(spans.length).fill(-1);
    const openByName = new Map();
    for (let k = 0; k < spans.length; k += 1) {
        const t = spans[k];
        if (t.kind !== "tag" || !t.name || t.name === "!" || t.name === "?")
            continue;
        const box = CONTAINERS[t.name];
        if (t.closing) {
            if (box)
                depth[box] = Math.max(0, depth[box] - 1);
            const open = openByName.get(t.name);
            if (open && open.length)
                matched[open.pop()] = k;
            continue;
        }
        const family = ENDED_BY[t.name] ? ENDED_BY[t.name].depth : undefined;
        // the depth an element of this name sits at; for every name, the depth of each container kind
        depthAt[k] = family ? depth[family] : 0;
        for (const kind of ["table", "list", "dl"]) {
            const key = `${t.name}@${kind}${depth[kind]}`;
            const list = startsAt.get(key) || [];
            list.push(k);
            startsAt.set(key, list);
        }
        const anyKey = `${t.name}@`;
        const any = startsAt.get(anyKey) || [];
        any.push(k);
        startsAt.set(anyKey, any);
        if (box)
            depth[box] += 1;
        if (!VOID_ELEMENTS.has(t.name)) {
            const open = openByName.get(t.name) || [];
            open.push(k);
            openByName.set(t.name, open);
        }
    }
    // where an element inside one may set visibility:visible
    const visibleAt = [];
    const VISIBLE = /visibility\s*:\s*visible/gi;
    for (let m = VISIBLE.exec(body); m; m = VISIBLE.exec(body))
        visibleAt.push(m.index);
    const ranges = [];
    for (let k = 0; k < spans.length && ranges.length < MAX_HIDDEN; k += 1) {
        const t = spans[k];
        if (t.kind !== "tag" ||
            !t.name ||
            t.closing ||
            t.name === "!" ||
            t.name === "?")
            continue;
        const tag = body.slice(t.start, t.end);
        if (!MAY_HIDE.test(tag) || tag.includes("{%") || NOT_READ.has(t.name))
            continue;
        const isVoid = VOID_ELEMENTS.has(t.name);
        const close = isVoid ? -1 : matched[k];
        if (!isVoid && close < 0)
            continue;
        const end = isVoid ? t.end : spans[close].start;
        const style = ((0, email_service_links_1.attrSpan)(tag, "style") || { value: "" }).value;
        const display = declared(style, "display");
        const visibility = declared(style, "visibility");
        const hidden = (display !== undefined &&
            display.value === "none" &&
            !(!display.important && reaches(sheet.important, tag, t.name))) ||
            (visibility !== undefined &&
                visibility.value === "hidden" &&
                !sheet.visible &&
                !someBetween(visibleAt, t.end - 1, end)) ||
            (display === undefined &&
                (0, email_service_links_1.attrSpan)(tag, "hidden") !== undefined &&
                !reaches(sheet.display, tag, t.name));
        if (!hidden)
            continue;
        const ended = ENDED_BY[t.name];
        if (ended && !isVoid) {
            const kind = ended.depth;
            const endedFirst = ended.by.some((name) => someBetween(startsAt.get(kind ? `${name}@${kind}${depthAt[k]}` : `${name}@`), k, close));
            if (endedFirst)
                continue;
        }
        ranges.push(isVoid ? [t.start, t.end] : [t.end, end]);
    }
    return ranges;
};
/**
 * The markup with what never reaches a reader blanked out (same length, so
 * positions hold): the Liquid branches and captures that never render, then
 * the elements hidden inline. `html` is the email as written, for its
 * stylesheets (the markup given has them blanked).
 */
const withoutNeverRendered = (body, html) => {
    const liquid = blankRanges(body, neverRenderedLiquid(body));
    return blankRanges(liquid, hiddenInline(liquid, html));
};
/** Why a link that would work does not, as the end of a sentence (V2-L5). */
const NEVER_SHOWN_WHY = "is never shown to the reader: it is hidden, or inside Liquid that never renders";
/** Does this link take the reader to Encharge's page as written (shape 1)? */
const opensUnsubscribe = (a) => !a.inComment &&
    a.clickable &&
    Boolean(a.href) &&
    (0, exports.hrefIsUrlTag)(a.href.value.trim());
const auditUnsubscribe = (html, { plainText = false } = {}) => {
    if (plainText) {
        return ANY_TAG.test(String(html || ""))
            ? { working: true, linkWorks: true, via: "plain-text", suspects: [] }
            : { working: false, linkWorks: false, suspects: [] };
    }
    const source = String(html || "");
    const readable = withoutInert(source);
    const shown = withoutNeverRendered(readable, source);
    if (shown === readable)
        return auditMarkup(readable, []);
    // the links that would work as written and are never shown
    const stillWork = new Set((0, email_service_links_1.readAnchors)(shown)
        .filter(opensUnsubscribe)
        .map((a) => a.start));
    const audit = auditMarkup(shown, (0, email_service_links_1.readAnchors)(readable).filter((a) => opensUnsubscribe(a) && !stillWork.has(a.start)));
    if (!audit.working && auditMarkup(readable, []).working)
        audit.neverShown = true;
    return audit;
};
exports.auditUnsubscribe = auditUnsubscribe;
/** The audit of markup with its inert and never-shown places blanked; `hidden` are the links that would work and are never shown. */
const auditMarkup = (body, hidden) => {
    const hiddenAt = new Set(hidden.map((a) => a.start));
    let via;
    let degraded = false;
    // the links past the cap, counted once per distinct link (label, address, clickable), and not when the same
    // link is already listed: the notes name each distinct link once ("the same footer twice once")
    const listed = new Set();
    const omittedKeys = new Set();
    const suspects = [];
    for (const a of (0, email_service_links_1.readAnchors)(body)) {
        // a link never shown is named as such below, not as one with nothing to click
        if (a.inComment || hiddenAt.has(a.start))
            continue;
        const href = a.href ? a.href.value.trim() : "";
        if ((0, exports.hrefIsUrlTag)(href) && a.clickable) {
            via = via || "anchor";
            continue;
        }
        const degradedHere = a.clickable && (URL_TAG_THEN_QUESTION.test(href) || URL_TAG_IN_ADDRESS.test(href));
        if (degradedHere)
            degraded = true;
        const byLabel = (0, email_service_links_1.labelKind)(a.label);
        const byHref = (0, email_service_links_1.classifyServiceLink)(href, a.label);
        const looksLikeOne = (0, exports.hrefIsUrlTag)(href) ||
            URL_TAG_THEN_QUESTION.test(href) ||
            URL_TAG_IN_ADDRESS.test(href) ||
            (MISCASED_TAG.test(href) && !EXACT_TAG.test(href)) ||
            FILTERED_URL_TAG.test(href) ||
            LINK_TAG_IN_HREF.test(href) ||
            (byLabel !== undefined && byLabel !== "viewInBrowser") ||
            (byHref !== undefined && byHref.kind !== "viewInBrowser" && byHref.kind !== "stopTracking");
        // a degraded link is kept past the cap: it is the one the verdict rests on; the rest past it are counted
        // one distinct link: its label, address and whether it has something to click
        const key = `${a.label}\u0000${href}\u0000${a.clickable}`;
        if (looksLikeOne && !(suspects.length < 10 || degradedHere)) {
            if (!listed.has(key))
                omittedKeys.add(key);
        }
        else if (looksLikeOne) {
            listed.add(key);
            suspects.push(Object.assign({ label: a.label, href, why: why(href, a.clickable), clickable: a.clickable }, (degradedHere ? { degraded: true } : {})));
        }
    }
    for (const a of hidden) {
        const href = a.href.value.trim();
        const key = `${a.label}\u0000${href}\u0000hidden`;
        if (suspects.length >= 10) {
            if (!listed.has(key))
                omittedKeys.add(key);
        }
        else if (!listed.has(key)) {
            listed.add(key);
            suspects.push({
                label: a.label,
                href,
                why: NEVER_SHOWN_WHY,
                clickable: true,
                hidden: true,
            });
        }
    }
    if (!via) {
        for (const span of (0, email_service_links_1.markupSpans)(body)) {
            if (span.kind !== "text")
                continue;
            const text = body.slice(span.start, span.end);
            if (LINK_TAG_IN_COPY.test(text)) {
                via = "link-tag";
                break;
            }
            if (URL_TAG_IN_COPY.test(text)) {
                via = "url-in-copy";
                break;
            }
        }
    }
    // shape 4: only when nothing better is there
    if (!via && degraded)
        via = "degraded-link";
    // degraded links first (a stable sort keeps the rest in reading order)
    suspects.sort((x, y) => Number(Boolean(y.degraded)) - Number(Boolean(x.degraded)));
    return Object.assign(Object.assign(Object.assign({ working: Boolean(via), linkWorks: Boolean(via) && via !== "degraded-link" }, (via ? { via } : {})), { suspects }), (omittedKeys.size ? { suspectsOmitted: omittedKeys.size } : {}));
};
const checkEmailHasUnsubscribe = (html, opts = {}) => (0, exports.auditUnsubscribe)(html, opts).working;
exports.checkEmailHasUnsubscribe = checkEmailHasUnsubscribe;
/**
 * The check this file replaced, kept as the fallback of the runtime kill
 * switch (service_layer/can_spam_unsubscribe_switch): one of the unsubscribe
 * tags, written without spaces, anywhere in the HTML (in a comment or an
 * href in its whole-link form too). Staging's checkEmailHasUnsubscribe as it
 * was before PR #1148.
 */
const checkEmailHasUnsubscribeTagAnywhere = (html) => /{{person\.(unsubscribeURL|unsubscribeAllURL|managePreferencesURL|unsubscribeLink|unsubscribeAllLink|managePreferencesLink)}}/.test(String(html || ""));
exports.checkEmailHasUnsubscribeTagAnywhere = checkEmailHasUnsubscribeTagAnywhere;
/**
 * The sender's mailing address tag, as Liquid reads it: spaces and the `-`
 * whitespace control allowed inside the braces ({{ account.mailingAddressSingleLine }},
 * which converted footers write, and {{- account.mailingAddress -}}), no
 * filter, and the name as written (Liquid reads {{ACCOUNT.MAILINGADDRESS}} as
 * nothing).
 */
const ADDRESS_TAG = /\{\{-?\s*account\.(?:mailingAddress|mailingAddressSingleLine)\s*-?\}\}/;
const checkEmailHasAddress = (html) => ADDRESS_TAG.test(String(html || ""));
exports.checkEmailHasAddress = checkEmailHasAddress;
// ---------------------------------------------------------------------------
// Repairing the held links whose meaning is plain (V2-H4)
/** A service tag at the start of an href: its name without the URL or Link ending, and which ending it has. */
const SERVICE_TAG_AT_START = /^\{\{-?\s*person\.(unsubscribe|unsubscribeAll|managePreferences|viewInBrowser)(URL|Link)\s*-?\}\}(?=$|[&#])/;
/**
 * http:// or https:// (the scheme in any case, spaces after it) written before
 * a service tag, which already prints https://... The tag as Liquid reads it
 * (case and all): a mis-cased one prints nothing either way (F31).
 */
const SCHEME_BEFORE_SERVICE_TAG = /^[Hh][Tt][Tt][Pp][Ss]?:\/\/\s*(?=\{\{-?\s*person\.(?:unsubscribe|unsubscribeAll|managePreferences|viewInBrowser)(?:URL|Link)\s*-?\}\})/;
/**
 * The href to write instead of one the strict check holds although what it
 * means is plain, or undefined to leave it as written:
 *   - a whole-link tag ({{person.unsubscribeLink}}, unsubscribeAllLink,
 *     managePreferencesLink, viewInBrowserLink), alone or followed by & or #,
 *     becomes its URL tag ({{person.unsubscribeURL}}...): the tag prints a
 *     whole `<a>`, which breaks the href it is in. Unlayer's link picker
 *     offers it, and the import already rewires it so;
 *   - an http:// or https:// written before a service tag is dropped: the tag
 *     already prints https://..., so the reader got https://https://...
 * Nothing else is touched: a tag inside another address (the customer's
 * redirect), mis-cased or filtered, is the customer's to fix.
 */
const repairUnsubscribeHref = (href) => {
    let h = String(href !== null && href !== void 0 ? href : "").trim();
    const scheme = SCHEME_BEFORE_SERVICE_TAG.exec(h);
    if (scheme)
        h = h.slice(scheme[0].length);
    const tag = SERVICE_TAG_AT_START.exec(h);
    if (tag && tag[2] === "Link")
        return `{{person.${tag[1]}URL}}${h.slice(tag[0].length)}`;
    return scheme ? h : undefined;
};
exports.repairUnsubscribeHref = repairUnsubscribeHref;
/**
 * The hrefs of the links that are not <a>: a VML shape's (Unlayer writes each
 * button's Outlook copy as <v:roundrect href> inside an Outlook conditional)
 * and an image map's <area>, read at the top level and one comment deep (F28).
 */
const otherLinkHrefs = (html) => {
    const out = [];
    const read = (source, offset, inComment) => {
        for (const s of (0, email_service_links_1.markupSpans)(source)) {
            if (s.kind === "comment") {
                const from = s.start + 4;
                const body = source.slice(from, Math.max(from, s.end - 3));
                if (!inComment && /<(?:v:|area\b)/i.test(body))
                    read(body, offset + from, true);
            }
            else if (s.kind === "tag" &&
                !s.closing &&
                s.name &&
                (s.name === "area" || s.name.startsWith("v:"))) {
                const href = (0, email_service_links_1.attrSpan)(source.slice(s.start, s.end), "href");
                if (href && href.start >= 0)
                    out.push({
                        value: href.value,
                        start: offset + s.start + href.start,
                        end: offset + s.start + href.end,
                    });
            }
        }
    };
    read(html, 0, false);
    return out;
};
/**
 * The markup with every link's href repaired (repairUnsubscribeHref), anchors
 * inside Outlook conditionals included, and a button's Outlook copy (VML) and
 * an image map's areas too; only the href values change, byte for byte
 * everything else stays as written. Running it again changes nothing.
 */
const repairUnsubscribeLinks = (html) => {
    const repaired = [];
    if (typeof html !== "string" || !html.includes("person."))
        return { html, repaired };
    const edits = [];
    const hrefs = [];
    for (const a of (0, email_service_links_1.readAnchors)(html))
        if (a.href && a.href.start >= 0)
            hrefs.push(a.href);
    hrefs.push(...otherLinkHrefs(html));
    for (const href of hrefs.sort((x, y) => x.start - y.start)) {
        const to = (0, exports.repairUnsubscribeHref)(href.value);
        if (to === undefined || to === href.value)
            continue;
        edits.push({ start: href.start, end: href.end, to });
        repaired.push({ from: href.value, to });
    }
    if (!edits.length)
        return { html, repaired };
    let out = "";
    let at = 0;
    for (const e of edits.sort((x, y) => x.start - y.start)) {
        out += html.slice(at, e.start) + e.to;
        at = e.end;
    }
    return { html: out + html.slice(at), repaired };
};
exports.repairUnsubscribeLinks = repairUnsubscribeLinks;
/** What the footer rule calls the footer, in the notes. */
const FOOTER_SAID = "The footer (the last 3 rows with words in them)";
/** Why no reader could see a link, as the middle of a sentence. */
const INVISIBLE_WHY = {
    hidden: "it was hidden",
    "no-size": "its text was set at size 0",
    "same-colour": "its text was the same colour as the background behind it",
    "tiny-picture": "its only picture was 1 pixel or smaller, or hidden",
};
/** Is this the address of an unsubscribe or preferences link: one of Encharge's URL tags, or a tag that prints the link? */
const isUnsubscribeHref = (href) => (0, exports.hrefIsUrlTag)(href) || ANY_TAG.test(String(href || ""));
exports.isUnsubscribeHref = isUnsubscribeHref;
/**
 * The note for a link the import took out because no reader could see it,
 * `times` the number of such links with the same words and address. An
 * unsubscribe link's says what that was, and what to do: add a visible one,
 * unless `stillWorks` (the email has another a reader can use).
 */
const invisibleLinkNote = (link, { q = asIs, times = 1, stillWorks = true } = {}) => {
    const what = link.label ? `the "${q((0, html_scan_1.clipText)(link.label, 60))}" link` : "an image link";
    const head = `Removed ${what} to ${q((0, html_scan_1.clipText)(link.href, 120))}${times > 1 ? ` (${times} times)` : ""}`;
    const why = `${INVISIBLE_WHY[link.reason] || INVISIBLE_WHY.hidden}, so no reader could see it.`;
    if (!(0, exports.isUnsubscribeHref)(link.href))
        return `${head}: ${why}`;
    return (`${head}, the email's unsubscribe link: ${why}` +
        (stillWorks
            ? " The email still has an unsubscribe link a reader can see."
            : " Add a visible unsubscribe link to the footer, linked to {{person.unsubscribeURL}}."));
};
exports.invisibleLinkNote = invisibleLinkNote;
/** Is this one of the import's notes for a link (not an unsubscribe link) taken out because no reader could see it? */
const isInvisibleLinkNote = (note) => (note.startsWith('Removed the "') || note.startsWith("Removed an image link to ")) && note.endsWith(", so no reader could see it.");
exports.isInvisibleLinkNote = isInvisibleLinkNote;
/** The notes for links taken out, one per distinct link (its words, address and reason), in order. */
const invisibleLinkNotes = (links, opts = {}) => {
    const counts = new Map();
    for (const link of links) {
        const key = `${link.label}\u0000${link.href}\u0000${link.reason}`;
        const seen = counts.get(key);
        if (seen)
            seen.times += 1;
        else
            counts.set(key, { link, times: 1 });
    }
    return Array.from(counts.values()).map(({ link, times }) => (0, exports.invisibleLinkNote)(link, Object.assign(Object.assign({}, opts), { times })));
};
exports.invisibleLinkNotes = invisibleLinkNotes;
/** The compliance facts as plain text only, for the app's API. */
const plainCompliance = (compliance) => {
    const { marked: _marked } = compliance, plain = __rest(compliance, ["marked"]);
    return plain;
};
exports.plainCompliance = plainCompliance;
const quoteLabel = (label, q = asIs) => label ? `The "${q((0, html_scan_1.clipText)(label, 50))}" link` : "An image link";
/**
 * Why the unsubscribe links do not work, one sentence each, at most `max`, the same footer twice once: what the
 * editor, the import and the MCP say. For a degraded email (shape 4) only the degraded link is named: the others
 * are not what the verdict rests on, and saying "Encharge never records the opt-out" under "readers can still
 * unsubscribe" contradicts it. `q` quotes the customer's values (marked_text quote() for the import).
 */
const unsubscribeLinkNotes = (audit, { q = asIs, max = 3 } = {}) => {
    var _a;
    if (audit.linkWorks)
        return [];
    const named = audit.via === "degraded-link" ? audit.suspects.filter((s) => s.degraded) : audit.suspects;
    const all = [];
    // each note once, in the order found (a Set: a degraded email keeps every degraded link, thousands in a big body)
    const said = new Set();
    // held only because the link is never shown, and no link of it is left to name (a whole-link tag in a hidden div)
    const say = (note) => {
        if (said.has(note))
            return;
        said.add(note);
        all.push(note);
    };
    if (audit.neverShown && !named.some((s) => s.hidden))
        say(`The unsubscribe link ${NEVER_SHOWN_WHY}.`);
    for (const s of named) {
        say(`${quoteLabel(s.label, q)} ${s.hidden ? NEVER_SHOWN_WHY : why(s.href, Boolean(s.clickable), q)}.`);
    }
    // never a silent cut: a customer who fixes the links named must know how many are left, counting the links
    // the audit found past its own cap (a degraded email names only its degraded links, all of them kept)
    const more = Math.max(0, all.length - max) + (audit.via === "degraded-link" ? 0 : (_a = audit.suspectsOmitted) !== null && _a !== void 0 ? _a : 0);
    if (!more)
        return all;
    return [...all.slice(0, max), `And ${more} more link${more === 1 ? "" : "s"} like ${more === 1 ? "it" : "these"}.`];
};
exports.unsubscribeLinkNotes = unsubscribeLinkNotes;
/**
 * The compliance facts of an email that was just imported, in words a
 * marketer can act on. Deliberately independent of the stored verdict
 * (EmailService.recordCanSpamCompliance), which approves every email outside
 * production and every email of an account older than the address rule: the
 * customer has to hear about a dead unsubscribe link in either case.
 */
const describeImportCompliance = ({ html, plainText = false, mailingAddress, findings, }) => {
    const body = String(html || "");
    const audit = (0, exports.auditUnsubscribe)(body, { plainText });
    const mailingAddressTag = (0, exports.checkEmailHasAddress)(body);
    const accountMailingAddressSet = mailingAddress === undefined ? undefined : String(mailingAddress || "").trim().length > 0;
    // each sentence with the customer values it quotes marked; the plain ones are these unmarked
    const problems = [];
    if (audit.working && !audit.linkWorks) {
        // the check passes (shape 4: the reader gets there), but not as the link should take them
        const detail = (0, exports.unsubscribeLinkNotes)(audit, { q: marked_text_1.quote })
            .map((note) => ` ${note}`)
            .join("");
        problems.push("THE UNSUBSCRIBE LINK DOES NOT WORK AS IT SHOULD. A reader can still reach Encharge's unsubscribe " +
            "page through it, so the email passes the compliance check, but not in one click or not directly." +
            detail +
            " In the editor, link the unsubscribe text to the special link \"Unsubscribe From Category\" " +
            "(or insert the \"Unsubscribe from category\" merge tag) so it always works.");
    }
    else if (!audit.working) {
        const detail = (0, exports.unsubscribeLinkNotes)(audit, { q: marked_text_1.quote })
            .map((note) => ` ${note}`)
            .join("");
        problems.push("NO WORKING UNSUBSCRIBE LINK. Nothing in this email opens Encharge's unsubscribe page, " +
            "so a reader cannot opt out and the email must not be sent as it is." +
            detail +
            (plainText
                ? " Add {{person.unsubscribeURL}} to the text before using it in a broadcast or flow."
                : " In the editor, link the unsubscribe text to the special link \"Unsubscribe From Category\" " +
                    "(or insert the \"Unsubscribe from category\" merge tag) before using it in a broadcast or flow."));
    }
    // the unsubscribe links the import took out because no reader could see them, each named
    if (findings && findings.invisibleUnsubscribe.length)
        problems.push(...(0, exports.invisibleLinkNotes)(findings.invisibleUnsubscribe, { q: marked_text_1.quote, stillWorks: audit.working }));
    if (!mailingAddressTag) {
        problems.push("No mailing address tag. CAN-SPAM requires the sender's postal address in every marketing " +
            "email, and Encharge's compliance check looks for {{account.mailingAddress}}: add it to " +
            "the footer, or the email can be held back from sending.");
    }
    else if (accountMailingAddressSet === false) {
        problems.push("The footer prints {{account.mailingAddress}}, but this account has no mailing address " +
            "set, so it prints nothing. Add it under Account settings before sending.");
    }
    // The footer rule: told only, for what the email has elsewhere (an email without it at all is told above).
    // Nothing is held or moved for it.
    if (findings && findings.footerLacks.unsubscribe && audit.working)
        problems.push(`${FOOTER_SAID} has no unsubscribe link: the email's unsubscribe link is higher up, and readers look for it ` +
            "at the bottom. Add one to the footer too, linked to {{person.unsubscribeURL}}.");
    if (findings && findings.footerLacks.address && mailingAddressTag)
        problems.push(`${FOOTER_SAID} does not print the mailing address: it is higher up in the email. Add ` +
            "{{account.mailingAddress}} to the footer, where readers and inbox providers look for it.");
    const unfilled = [];
    if (!plainText) {
        const seen = new Set();
        for (const a of (0, email_service_links_1.readAnchors)(body)) {
            if (a.inComment || !a.href)
                continue;
            const href = a.href.value.trim();
            // Liquid is Encharge's own syntax; the merge-tag report checks it
            if (/^\{[{%]/.test(href) || !(0, email_service_links_1.placeholderName)(href) || seen.has(href))
                continue;
            if ((0, email_service_links_1.classifyServiceLink)(href, a.label))
                continue;
            seen.add(href);
            unfilled.push(`${quoteLabel(a.label, marked_text_1.quote)} points at ${(0, marked_text_1.quote)(href)}, a placeholder Encharge does not fill, so it goes nowhere.`);
            if (unfilled.length >= 8)
                break;
        }
    }
    const inCopy = (0, email_service_links_1.unfilledTextPlaceholders)(body);
    if (inCopy.length) {
        const listed = inCopy
            .slice(0, 8)
            .map((p) => (0, marked_text_1.quote)(p))
            .join(", ");
        unfilled.push(`The copy still contains ${listed}${inCopy.length > 8 ? " and more" : ""}: ` +
            `${inCopy.length === 1 ? "a placeholder" : "placeholders"} Encharge does not fill, so readers see ` +
            `${inCopy.length === 1 ? "it" : "them"} exactly as written.`);
    }
    return Object.assign(Object.assign({ 
        // what the import reports is whether the link WORKS (the check itself needs only the tag)
        unsubscribeLink: audit.linkWorks, unsubscribeReachable: audit.working, mailingAddressTag }, (accountMailingAddressSet === undefined ? {} : { accountMailingAddressSet })), { problems: problems.map(marked_text_1.unmark), unfilled: unfilled.map(marked_text_1.unmark), marked: { problems, unfilled } });
};
exports.describeImportCompliance = describeImportCompliance;
//# sourceMappingURL=email_can_spam_compliance_helper.js.map